import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateContractModal from '@modals/CreateContractModal'
import EditProviderCustomInvoiceModal from '@modals/EditProviderCustomInvoiceModal'
import EditProviderTradingDaysModal from '@modals/EditProviderTradingDaysModal'
import GenerateReportModal from '@modals/GenerateReportModal'
import ManageActivityModal from '@modals/ManageActivityModal'
import ManageProviderApiSettingsModal from '@modals/ManageProviderApiSettingsModal'
import ManageProviderOptionsModal from '@modals/ManageProviderOptionsModal'
import ManageProviderOrderDeliveryModal from '@modals/ManageProviderOrderDeliveryModal'
import ManageEntityQuickLinksModal from '@modals/ManageEntityQuickLinksModal'
import UploadEntityAssetsModal from '@modals/UploadEntityAssetsModal'
import UploadProviderLogoModal from '@modals/UploadProviderLogoModal'

import type { ProviderModel } from '@models/types'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateContractModal: false,
  showEditProviderCustomInvoiceModal: false,
  showEditProviderTradingDaysModal: false,
  showGenerateReportModal: false,
  showManageActivityModal: false,
  showManageProviderApiSettingsModal: false,
  showManageProviderOptionsModal: false,
  showManageProviderOrderDeliveryModal: false,
  showManageEntityQuickLinksModal: false,
  showUploadEntityAssetsModal: false,
  showUploadProviderLogoModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: string, setState: SetState) => {
  const componentCallbacks = {
    CreateContractModal: {
      closeModal: () => setState({ showCreateContractModal: false }),
      createContract: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    EditProviderCustomInvoiceModal: {
      closeModal: () => setState({ showEditProviderCustomInvoiceModal: false }),
      updateProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    EditProviderTradingDaysModal: {
      closeModal: () => setState({ showEditProviderTradingDaysModal: false }),
      updateProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    GenerateReportModal: {
      closeModal: () => setState({ showGenerateReportModal: false }),
      generateReport: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageActivityModal: {
      closeModal: () => setState({ showManageActivityModal: false }),
    },
    ManageProviderApiSettingsModal: {
      closeModal: () => setState({ showManageProviderApiSettingsModal: false }),
      updateProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageProviderOrderDeliveryModal: {
      closeModal: () => setState({ showManageProviderOrderDeliveryModal: false }),
      updateProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageProviderOptionsModal: {
      closeModal: () => setState({ showManageProviderOptionsModal: false }),
      updateProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageEntityQuickLinksModal: {
      closeModal: () => setState({ showManageEntityQuickLinksModal: false }),
      updateEntity: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    UploadEntityAssetsModal: {
      closeModal: () => setState({ showUploadEntityAssetsModal: false }),
      deleteAttachment: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    UploadProviderLogoModal: {
      closeModal: () => setState({ showUploadProviderLogoModal: false }),
      updateProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  // @ts-expect-error TS7053
  return componentCallbacks[component] || {}
}

const Details = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateContractModal,
    showEditProviderCustomInvoiceModal,
    showEditProviderTradingDaysModal,
    showGenerateReportModal,
    showManageActivityModal,
    showManageProviderApiSettingsModal,
    showManageProviderOptionsModal,
    showManageProviderOrderDeliveryModal,
    showManageEntityQuickLinksModal,
    showUploadEntityAssetsModal,
    showUploadProviderLogoModal,
  } = state

  const { providerId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      // @ts-expect-error TS7006
      deleteAttachment: payload => handleCallbackAction(payload),
      // @ts-expect-error TS7006
      deleteContract: payload => handleCallbackAction(payload),
      // @ts-expect-error TS7006
      exportProvider: payload => handleCallbackAction(payload),
      // @ts-expect-error TS7006
      showCreateContractModal: (payload) => {
        setModalData('CreateContractModal', payload)
        setState({ showCreateContractModal: true })
      },
      // @ts-expect-error TS7006
      showEditProviderCustomInvoiceModal: (payload) => {
        setModalData('EditProviderCustomInvoiceModal', payload)
        setState({ showEditProviderCustomInvoiceModal: true })
      },
      // @ts-expect-error TS7006
      showEditProviderTradingDaysModal: (payload) => {
        setModalData('EditProviderTradingDaysModal', payload)
        setState({ showEditProviderTradingDaysModal: true })
      },
      // @ts-expect-error TS7006
      showGenerateReportModal: (payload) => {
        setModalData('GenerateReportModal', payload)
        setState({ showGenerateReportModal: true })
      },
      // @ts-expect-error TS7006
      showManageActivityModal: (payload) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
      // @ts-expect-error TS7006
      showManageProviderApiSettingsModal: (payload) => {
        setModalData('ManageProviderApiSettingsModal', payload)
        setState({ showManageProviderApiSettingsModal: true })
      },
      // @ts-expect-error TS7006
      showManageProviderOrderDeliveryModal: (payload) => {
        setModalData('ManageProviderOrderDeliveryModal', payload)
        setState({ showManageProviderOrderDeliveryModal: true })
      },
      // @ts-expect-error TS7006
      showManageProviderOptionsModal: (payload) => {
        setModalData('ManageProviderOptionsModal', payload)
        setState({ showManageProviderOptionsModal: true })
      },
      // @ts-expect-error TS7006
      showUploadEntityAssetsModal: (payload) => {
        setModalData('UploadEntityAssetsModal', payload)
        setState({ showUploadEntityAssetsModal: true })
      },
      showManageEntityQuickLinksModal: (payload: { provider: ProviderModel }) => {
        setModalData('ManageEntityQuickLinksModal', payload)
        setState({ showManageEntityQuickLinksModal: true })
      },
      // @ts-expect-error TS7006
      showUploadProviderLogoModal: (payload) => {
        setModalData('UploadProviderLogoModal', payload)
        setState({ showUploadProviderLogoModal: true })
      },
      updateProvider: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent providerId={providerId} />

        <CreateContractModal
          callbacks={callbacks('CreateContractModal', setState)}
          showModal={showCreateContractModal}
        />

        <EditProviderCustomInvoiceModal
          callbacks={callbacks('EditProviderCustomInvoiceModal', setState)}
          showModal={showEditProviderCustomInvoiceModal}
        />

        <EditProviderTradingDaysModal
          callbacks={callbacks('EditProviderTradingDaysModal', setState)}
          showModal={showEditProviderTradingDaysModal}
        />

        <GenerateReportModal
          callbacks={callbacks('GenerateReportModal', setState)}
          showModal={showGenerateReportModal}
        />

        <ManageActivityModal
          callbacks={callbacks('ManageActivityModal', setState)}
          showModal={showManageActivityModal}
        />

        <ManageProviderApiSettingsModal
          callbacks={callbacks('ManageProviderApiSettingsModal', setState)}
          showModal={showManageProviderApiSettingsModal}
        />

        <ManageProviderOrderDeliveryModal
          callbacks={callbacks('ManageProviderOrderDeliveryModal', setState)}
          showModal={showManageProviderOrderDeliveryModal}
        />

        <ManageProviderOptionsModal
          callbacks={callbacks('ManageProviderOptionsModal', setState)}
          showModal={showManageProviderOptionsModal}
        />

        <ManageEntityQuickLinksModal
          callbacks={callbacks('ManageEntityQuickLinksModal', setState)}
          showModal={showManageEntityQuickLinksModal}
        />

        <UploadEntityAssetsModal
          callbacks={callbacks('UploadEntityAssetsModal', setState)}
          showModal={showUploadEntityAssetsModal}
        />

        <UploadProviderLogoModal
          callbacks={callbacks('UploadProviderLogoModal', setState)}
          showModal={showUploadProviderLogoModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
